import React, { useMemo, createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
// Phantom and Solflare are auto-detected via Wallet Standard
// Only add adapters for wallets that don't support Wallet Standard
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { NETWORKS } from './utils/constants';
import logger from './utils/logger';

// Import wallet adapter CSS
import '@solana/wallet-adapter-react-ui/styles.css';

// Storage key for network persistence
const NETWORK_STORAGE_KEY = 'pf-network';

// Network context for switching between mainnet/devnet
const NetworkContext = createContext({
  network: 'mainnet',
  setNetwork: () => {},
  endpoint: NETWORKS.mainnet.endpoint,
  rpcEndpoint: NETWORKS.mainnet.endpoint,
  backendUrl: NETWORKS.mainnet.backendUrl,
  isMainnet: true,
});

export const useNetwork = () => useContext(NetworkContext);

// Get initial network from localStorage, default to mainnet
const getInitialNetwork = () => {
  try {
    const stored = localStorage.getItem(NETWORK_STORAGE_KEY);
    if (stored === 'devnet') return 'devnet';
  } catch (error) {
    // localStorage may be unavailable (SSR, private browsing, etc.)
    // Silently default to mainnet
  }
  return 'mainnet'; // Default to mainnet for safety
};

export default function AppWalletProvider({ children }) {
  const [network, setNetworkState] = useState(getInitialNetwork);

  const endpoint = useMemo(() => {
    return NETWORKS[network]?.endpoint || NETWORKS.mainnet.endpoint;
  }, [network]);

  const backendUrl = useMemo(() => {
    return NETWORKS[network]?.backendUrl || NETWORKS.mainnet.backendUrl;
  }, [network]);

  // Configure wallet adapters
  // Phantom is auto-detected via Wallet Standard (don't add explicit adapter)
  // Solflare adapter added for compatibility with older versions
  const wallets = useMemo(() => [new SolflareWalletAdapter()], []);

  // Persist network changes to localStorage
  const handleNetworkChange = useCallback(newNetwork => {
    const validNetwork = newNetwork === 'devnet' ? 'devnet' : 'mainnet';
    setNetworkState(validNetwork);
    try {
      localStorage.setItem(NETWORK_STORAGE_KEY, validNetwork);
    } catch (error) {
      // localStorage may be unavailable - network state still works in memory
    }
  }, []);

  // Sync with localStorage on mount (handles multi-tab scenarios)
  useEffect(() => {
    const handleStorage = e => {
      if (e.key === NETWORK_STORAGE_KEY) {
        const newValue = e.newValue === 'devnet' ? 'devnet' : 'mainnet';
        setNetworkState(newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const isMainnet = network === 'mainnet';

  const networkContextValue = useMemo(
    () => ({
      network,
      setNetwork: handleNetworkChange,
      endpoint,
      rpcEndpoint: endpoint,
      backendUrl,
      isMainnet,
    }),
    [network, handleNetworkChange, endpoint, backendUrl, isMainnet]
  );

  return (
    <NetworkContext.Provider value={networkContextValue}>
      <ConnectionProvider endpoint={endpoint}>
        <WalletProvider
          wallets={wallets}
          autoConnect={true}
          onError={(error) => {
            // Enhanced wallet error handling
            const errorInfo = {
              name: error.name,
              message: error.message,
              stack: error.stack?.split('\n')[0] // First line only for brevity
            };
            
            // Don't spam console for expected transient errors
            if (error.name === 'WalletNotReadyError') {
              // Wallet still initializing - will auto-retry
              return;
            }
            
            if (error.name === 'WalletLoadError') {
              // Wallet extension not loaded yet
              return;
            }
            
            // Log actual connection failures
            if (error.name === 'WalletConnectionError') {
              console.error('🔌 Wallet connection error:', errorInfo);
              logger.warn('Wallet connection error', errorInfo);
              return;
            }
            
            // Log all other errors
            console.error('💥 Wallet error:', errorInfo);
            logger.warn('Wallet error', errorInfo);
          }}
        >
          <WalletModalProvider>{children}</WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </NetworkContext.Provider>
  );
}
